/*
 * Rock Off Retail: "Back to <listing>" link on the product page (1 Oct 2026).
 *
 * Listing pages (collection, search, hub, band/character/spiral index and the
 * coming-soon pages) record their exact URL in sessionStorage while the
 * shopper is on them. The product page link (snippets/rockoff-back-link.liquid)
 * then points back at that exact URL, so page number, filters and sort survive.
 *
 * Page kind comes from <meta name="ro-page-context"> in layout/theme.liquid:
 *   data-type  = listing | product | other
 *   data-kind  = collection | search | hub | page   (listing only)
 *   data-label = display name of the listing        (listing only)
 *
 * Rules:
 *   listing  -> keep the stored entry current (load, product-link click, pagehide;
 *               USF rewrites the URL client-side so location.href is read at the
 *               moment of leaving)
 *   product  -> keep whatever is stored; swap the link href + label if valid
 *   other    -> clear the stored entry so a stale listing never shows later
 *
 * Plain <a> link, no history.back(). All sessionStorage access is try/catch
 * guarded (private mode). Labels go in through textContent only. Only
 * same-origin URLs are ever allowed into href.
 */
(function (root) {
  'use strict';

  var KEY = 'ro_last_listing';
  var MAX_AGE_MS = 12 * 60 * 60 * 1000;
  var MAX_LABEL = 120;
  var PLACEHOLDER = '__RO_LABEL__';

  function cleanLabel(value) {
    if (typeof value !== 'string') return '';
    // No en/em dashes in customer copy: listing names are shown as typed
    // elsewhere, so normalise them to a plain hyphen here.
    return value.replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ').trim().slice(0, MAX_LABEL);
  }

  // Returns a validated {url, label, kind, ts} or null.
  function sanitizeStored(raw, origin, now) {
    var data = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (!data || typeof data !== 'object' || typeof data.url !== 'string') return null;
    var parsed;
    try {
      parsed = new URL(data.url, origin);
    } catch (e) {
      return null;
    }
    if (parsed.origin !== origin) return null;
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    // A product page is never a listing; guards against loops.
    if (/\/products\//.test(parsed.pathname)) return null;
    var ts = Number(data.ts);
    if (!isFinite(ts) || ts <= 0) return null;
    if (now - ts > MAX_AGE_MS || ts - now > 60 * 1000) return null;
    var kind = data.kind === 'search' ? 'search' : 'page';
    var label = cleanLabel(data.label);
    if (kind !== 'search' && !label) return null;
    return { url: parsed.href, label: label, kind: kind, ts: ts };
  }

  // What to do with storage for a given page type.
  function planFor(type) {
    if (type === 'listing') return 'record';
    if (type === 'product') return 'keep';
    return 'clear';
  }

  function buildEntry(ctx, href, now) {
    if (!ctx || ctx.type !== 'listing') return null;
    var kind = ctx.kind === 'search' ? 'search' : 'page';
    var label = cleanLabel(ctx.label);
    if (kind !== 'search' && !label) return null;
    return { url: href, label: label, kind: kind, ts: now };
  }

  function fillTemplate(tpl, label) {
    return String(tpl).split(PLACEHOLDER).join(label);
  }

  var api = {
    sanitizeStored: sanitizeStored,
    planFor: planFor,
    buildEntry: buildEntry,
    fillTemplate: fillTemplate,
    cleanLabel: cleanLabel,
    KEY: KEY
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (!root || !root.document) return;

  var doc = root.document;

  function readCtx() {
    var meta = doc.querySelector('meta[name="ro-page-context"]');
    if (!meta) return null;
    return {
      type: meta.getAttribute('data-type') || 'other',
      kind: meta.getAttribute('data-kind') || '',
      label: meta.getAttribute('data-label') || ''
    };
  }

  function storageGet() {
    try {
      return root.sessionStorage.getItem(KEY);
    } catch (e) {
      return null;
    }
  }
  function storageSet(value) {
    try {
      root.sessionStorage.setItem(KEY, value);
    } catch (e) {}
  }
  function storageClear() {
    try {
      root.sessionStorage.removeItem(KEY);
    } catch (e) {}
  }

  function record(ctx) {
    var entry = buildEntry(ctx, root.location.href, Date.now());
    if (entry) storageSet(JSON.stringify(entry));
  }

  function applyToLinks() {
    var links = doc.querySelectorAll('[data-ro-back-link]');
    if (!links.length) return;
    var stored = sanitizeStored(storageGet(), root.location.origin, Date.now());
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (link.closest && link.closest('quick-add-modal')) continue;
      var textEl = link.querySelector('[data-ro-back-link-text]');
      if (!textEl) continue;
      // Remember the server-rendered fallback once, so a bfcache restore after
      // storage was cleared or expired shows the hub / Shop all link again.
      if (!link.hasAttribute('data-ro-fallback-href')) {
        link.setAttribute('data-ro-fallback-href', link.getAttribute('href') || '');
        link.setAttribute('data-ro-fallback-text', textEl.textContent);
      }
      if (!stored) {
        link.setAttribute('href', link.getAttribute('data-ro-fallback-href'));
        textEl.textContent = link.getAttribute('data-ro-fallback-text');
        continue;
      }
      var text;
      if (stored.kind === 'search') {
        text = link.getAttribute('data-search-text');
      } else {
        var tpl = link.getAttribute('data-template');
        text = tpl ? fillTemplate(tpl, stored.label) : '';
      }
      if (!text) {
        link.setAttribute('href', link.getAttribute('data-ro-fallback-href'));
        textEl.textContent = link.getAttribute('data-ro-fallback-text');
        continue;
      }
      link.setAttribute('href', stored.url);
      textEl.textContent = text;
    }
  }

  // Runs on load and again on every bfcache restore (pageshow persisted), so
  // a restored cart clears, a restored listing re-records and a restored
  // product re-reads storage.
  function run(ctx) {
    var plan = planFor(ctx.type);
    if (plan === 'clear') storageClear();
    else if (plan === 'record') record(ctx);
    else applyToLinks();
  }

  function init() {
    var ctx = readCtx();
    if (!ctx) return;
    run(ctx);
    root.addEventListener('pageshow', function (event) {
      if (event.persisted) run(ctx);
    });
    if (planFor(ctx.type) !== 'record') return;

    // USF rewrites the URL client-side (page, filters, sort), so record the
    // current href at the moment a product link is used: left click, middle
    // click (auxclick, no click event fires) and right click / long press
    // before "open in new tab" (contextmenu). pagehide covers the rest.
    function onProductLinkUse(event) {
      var target = event.target;
      var a = target && target.closest ? target.closest('a[href]') : null;
      if (!a) return;
      var path = '';
      try {
        path = new URL(a.getAttribute('href'), root.location.href).pathname;
      } catch (e) {
        return;
      }
      if (/\/products\//.test(path)) record(ctx);
    }
    doc.addEventListener('click', onProductLinkUse, true);
    doc.addEventListener('auxclick', onProductLinkUse, true);
    doc.addEventListener('contextmenu', onProductLinkUse, true);
    root.addEventListener('pagehide', function () {
      record(ctx);
    });
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})(typeof window !== 'undefined' ? window : null);
